import React, { useState } from 'react';
import { App, Button, Card, Descriptions, List, Progress, Result, Space, Spin, Tag, Typography, Upload } from 'antd';
import { CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import hrmsService from '@/services/hrms.service';

const { Title, Text } = Typography;
const MAX_BYTES = 10 * 1024 * 1024;
const STATUS_COLOR: Record<string, string> = {
  Open: 'blue',
  InProgress: 'processing',
  Completed: 'success',
  Pending: 'processing',
  Done: 'success',
  Skipped: 'default',
};
const hrmsStatusColor = (s?: string) => STATUS_COLOR[s || ''] || 'default';

const OnboardingPortalPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [busyTask, setBusyTask] = useState<string | null>(null);
  const key = ['public-onboarding', token];

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => hrmsService.getPublicOnboarding(token!),
    enabled: Boolean(token),
  });

  const run = async (taskId: string, fn: () => Promise<any>, ok: string) => {
    setBusyTask(taskId);
    try {
      const fresh = await fn();
      queryClient.setQueryData(key, fresh);
      message.success(ok);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Something went wrong');
    } finally {
      setBusyTask(null);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ maxWidth: 640, margin: '48px auto', padding: 24 }}>
        <Result status="404" title="Onboarding link not found or expired" subTitle="Please contact HR for a new link." />
      </div>
    );
  }

  const tasks: any[] = Array.isArray(data.tasks) ? data.tasks : [];
  const mine = tasks.filter((t) => t.new_hire_action);
  const others = tasks.filter((t) => !t.new_hire_action);
  const completed = data.status === 'Completed';

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '32px 24px' }}>
      <Title level={2} style={{ marginBottom: 0 }}>
        Welcome{data.employee_name ? `, ${data.employee_name}` : ''}
      </Title>
      <Text type="secondary">{data.company_name ? `${data.company_name} · ` : ''}{data.plan_number}</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
          <Descriptions.Item label="Join date">{data.join_date || '—'}</Descriptions.Item>
          <Descriptions.Item label="Probation ends">{data.probation_end_date || '—'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={hrmsStatusColor(data.status)}>{data.status}</Tag>
          </Descriptions.Item>
        </Descriptions>
        <Progress percent={Number(data.progress_pct || 0)} />
      </Card>

      <Card title="Things we need from you" style={{ marginBottom: 16 }}>
        <List
          dataSource={mine}
          locale={{ emptyText: 'Nothing needed from you right now' }}
          renderItem={(t: any) => {
            const done = t.status === 'Done' || t.status === 'Skipped';
            const busy = busyTask === t.id;
            return (
              <List.Item
                actions={
                  done || completed
                    ? [<Tag key="s" color={hrmsStatusColor(t.status)}>{t.status === 'Done' ? 'Done' : 'Not required'}</Tag>]
                    : t.task_type === 'Acknowledgement'
                      ? [
                          <Button
                            key="a"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={busy}
                            onClick={() => run(t.id, () => hrmsService.acknowledgePublicOnboardingTask(token!, t.id), 'Acknowledged')}
                          >
                            I acknowledge
                          </Button>,
                        ]
                      : [
                          <Upload
                            key="u"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                            showUploadList={false}
                            beforeUpload={(file) => {
                              if (file.size > MAX_BYTES) {
                                message.error('File size exceeds 10MB limit');
                                return Upload.LIST_IGNORE;
                              }
                              run(t.id, () => hrmsService.uploadPublicOnboardingTask(token!, t.id, file), 'Document uploaded');
                              return false;
                            }}
                          >
                            <Button type="primary" icon={<UploadOutlined />} loading={busy}>
                              Upload
                            </Button>
                          </Upload>,
                        ]
                }
              >
                <List.Item.Meta
                  title={
                    <Space>
                      {t.task_name}
                      {t.is_mandatory && !done && <Tag color="red">Required</Tag>}
                    </Space>
                  }
                  description={
                    <Space size="small">
                      <span>{t.task_type === 'Acknowledgement' ? 'Read and acknowledge' : 'Upload a document (PDF, Word or photo)'}</span>
                      {t.due_date && !done && <Text type="secondary">· due {String(t.due_date).slice(0, 10)}</Text>}
                      {t.has_document && <Tag>File received</Tag>}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>

      {others.length > 0 && (
        <Card title="What the company is preparing for you" size="small">
          <List
            size="small"
            dataSource={others}
            renderItem={(t: any) => (
              <List.Item extra={<Tag color={hrmsStatusColor(t.status)}>{t.status === 'Skipped' ? 'N/A' : t.status}</Tag>}>{t.task_name}</List.Item>
            )}
          />
        </Card>
      )}
      <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
        Contact HR if you need help completing a task.
      </Text>
    </div>
  );
};

export default OnboardingPortalPage;
